import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { hapticSelection } from '../../../lib/haptics';
import { Home as Comedy, Sword, Heart, Ghost, Film, Drama, Rocket, FileVideo, Check } from 'lucide-react';

interface GenreOption {
  id: string;
  name: string;
  icon: React.ReactNode;
  /** hue/sat/light for this genre's muted accent — kept subtle to fit the dark theme */
  hue: number;
  sat: number;
  light: number;
}

const genres: GenreOption[] = [
  { id: 'comedy', name: 'Comedy', icon: <Comedy className="h-6 w-6" />, hue: 43, sat: 82, light: 58 },
  { id: 'thriller', name: 'Thriller', icon: <Sword className="h-6 w-6" />, hue: 358, sat: 70, light: 58 },
  { id: 'action', name: 'Action', icon: <Film className="h-6 w-6" />, hue: 24, sat: 76, light: 56 },
  { id: 'drama', name: 'Drama', icon: <Drama className="h-6 w-6" />, hue: 268, sat: 38, light: 66 },
  { id: 'scifi-fantasy', name: 'Sci-Fi/Fantasy', icon: <Rocket className="h-6 w-6" />, hue: 205, sat: 50, light: 62 },
  { id: 'romance', name: 'Romance', icon: <Heart className="h-6 w-6" />, hue: 333, sat: 52, light: 66 },
  { id: 'horror', name: 'Horror', icon: <Ghost className="h-6 w-6" />, hue: 155, sat: 10, light: 58 },
  { id: 'documentary', name: 'Documentary', icon: <FileVideo className="h-6 w-6" />, hue: 152, sat: 30, light: 52 },
];

interface Props {
  selectedGenres: string[];
  onSelect: (genres: string[]) => void;
}

export function GenreSelection({ selectedGenres: initialSelectedGenres, onSelect }: Props) {
  const [selectedGenres, setSelectedGenres] = useState<string[]>(initialSelectedGenres);
  const [error, setError] = useState<string | null>(null);

  const handleGenreToggle = (genreId: string) => {
    void hapticSelection();
    setSelectedGenres(prev => {
      const newSelection = prev.includes(genreId)
        ? prev.filter(id => id !== genreId)
        : [...prev, genreId];
      setError(null);
      return newSelection;
    });
  };

  const handleSubmit = () => {
    if (selectedGenres.length === 0) {
      setError('Please select at least one genre');
      return;
    }
    onSelect(selectedGenres);
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          What are you in the <span className="text-gradient-dusk">mood</span> for?
        </h2>
        <p className="text-muted-foreground">
          Choose as many genres as you like.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {genres.map((genre) => {
          const isSelected = selectedGenres.includes(genre.id);
          const tint = `hsl(${genre.hue} ${genre.sat}% ${genre.light}%)`;

          return (
            <button
              key={genre.id}
              onClick={() => handleGenreToggle(genre.id)}
              className="select-card flex flex-col items-center p-5"
              style={isSelected ? {
                borderColor: `hsl(${genre.hue} ${genre.sat}% ${genre.light}% / 0.5)`,
                backgroundColor: `hsl(${genre.hue} ${genre.sat}% ${genre.light}% / 0.1)`,
              } : undefined}
              aria-pressed={isSelected}
            >
              {isSelected && (
                <span
                  className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full text-primary-foreground"
                  style={{ backgroundColor: tint }}
                >
                  <Check className="h-3 w-3" strokeWidth={3.5} />
                </span>
              )}
              <div
                className="mb-3 rounded-2xl border border-white/[0.07] bg-white/[0.04] p-3 transition-colors duration-200"
                style={{ color: tint }}
              >
                {genre.icon}
              </div>
              <span
                className={cn(
                  'text-sm font-medium transition-colors duration-200',
                  !isSelected && 'text-foreground'
                )}
                style={isSelected ? { color: tint } : undefined}
              >
                {genre.name}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-center text-sm text-red-400">{error}</p>
      )}

      <button onClick={handleSubmit} className="btn-marquee w-full px-4 py-3.5 text-base">
        Continue
      </button>
    </div>
  );
}
